import React, { useState } from 'react';
import { X, Plus, CheckCircle, Loader2 } from 'lucide-react';
import { useCreateReportingPeriod, useReleaseGrades } from '../../hooks/useGrading';

const ReportingPeriodsModal = ({ classId, periods, onClose }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    periodName: '',
    periodType: 'quarter',
    startDate: '',
    endDate: ''
  });

  const createPeriod = useCreateReportingPeriod();
  const releaseGrades = useReleaseGrades();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPeriod.mutateAsync({ classId, data: formData });
      setShowAddForm(false);
      setFormData({ periodName: '', periodType: 'quarter', startDate: '', endDate: '' });
    } catch (err) {
      console.error('Failed to create period:', err);
    }
  };

  const handleRelease = async (periodId) => {
    if (window.confirm('Release grades to parents for this period?')) {
      try {
        await releaseGrades.mutateAsync(periodId);
      } catch (err) {
        console.error('Failed to release grades:', err);
      }
    }
  };

  const getPeriodTypeLabel = (type) => {
    const labels = {
      week: 'Week',
      biweek: 'Bi-week',
      month: 'Month',
      quarter: 'Quarter',
      semester: 'Semester',
      year: 'Year',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Reporting Periods</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Add Period Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mb-6 btn-secondary flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Reporting Period</span>
            </button>
          )}

          {/* Add Period Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-6 bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-gray-900">New Reporting Period</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="form-label">Period Name *</label>
                  <input
                    type="text"
                    value={formData.periodName}
                    onChange={(e) => setFormData({...formData, periodName: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Q1 2024"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Type *</label>
                  <select
                    value={formData.periodType}
                    onChange={(e) => setFormData({...formData, periodType: e.target.value})}
                    className="form-input"
                    required
                  >
                    <option value="week">Week</option>
                    <option value="biweek">Bi-week</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                    <option value="semester">Semester</option>
                    <option value="year">Year</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">End Date *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createPeriod.isPending}
                  className="btn-primary text-sm"
                >
                  {createPeriod.isPending ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    'Create Period'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Periods List */}
          <div className="space-y-3">
            {periods.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No reporting periods created yet.</p>
                <p className="text-sm">Add a period to organize your grades.</p>
              </div>
            ) : (
              periods.map(period => (
                <div 
                  key={period.id}
                  className="border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-gray-900">{period.periodName}</h3>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        {getPeriodTypeLabel(period.periodType)}
                      </span>
                      {period.gradesReleased && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center">
                          <CheckCircle size={12} className="mr-1" />
                          Released
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(period.startDate).toLocaleDateString()} - {new Date(period.endDate).toLocaleDateString()}
                    </p>
                    {period.gradesReleased && period.releasedAt && (
                      <p className="text-xs text-gray-400 mt-1">
                        Released on {new Date(period.releasedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {!period.gradesReleased && (
                    <button
                      onClick={() => handleRelease(period.id)}
                      disabled={releaseGrades.isPending}
                      className="btn-primary text-sm"
                    >
                      {releaseGrades.isPending ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        'Release Grades'
                      )}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportingPeriodsModal;
